// Mobile web: keep the focused text field visible above the on-screen keyboard.
// Lauren (Brave, 2026-11-02): tapping Email / Password on sign-in left the field under
// the keyboard. Two parts:
//  1. viewport `interactive-widget=resizes-content` so Chrome / Brave on Android shrink
//     the page when the keyboard opens (the default only shrinks the visual viewport,
//     and our full-height app never scrolls the field up by itself);
//  2. on focus, and again when the keyboard finishes resizing the viewport, scroll the
//     field into the middle of what is still visible (iOS Safari keeps the layout size).

const TEXT_INPUT_TYPES = new Set([
  '', 'text', 'email', 'password', 'search', 'tel', 'url', 'number',
  'date', 'datetime-local', 'month', 'time', 'week',
]);

export function isTextEntryElement(el) {
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea') return true;
  if (el.isContentEditable) return true;
  if (tag !== 'input') return false;
  const type = String(el.getAttribute?.('type') || el.type || '').toLowerCase();
  return TEXT_INPUT_TYPES.has(type);
}

export function withResizesContent(content) {
  const parts = String(content || '')
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p && !/^interactive-widget\s*=/i.test(p));
  parts.push('interactive-widget=resizes-content');
  return parts.join(', ');
}

// True when the element sits outside the part of the screen the keyboard leaves visible.
export function isHiddenByKeyboard(rect, viewport, margin = 16) {
  if (!rect || !viewport) return false;
  const top = viewport.offsetTop || 0;
  const bottom = top + viewport.height;
  return rect.bottom > bottom - margin || rect.top < top + margin;
}

export function installMobileKeyboardFocus(win = typeof window !== 'undefined' ? window : null) {
  if (!win || !win.document || win.__vrcKeyboardFocusInstalled) return () => {};
  const coarse = typeof win.matchMedia === 'function' && win.matchMedia('(pointer: coarse)').matches;
  if (!coarse) return () => {};
  win.__vrcKeyboardFocusInstalled = true;
  const doc = win.document;

  const meta = doc.querySelector('meta[name="viewport"]');
  if (meta) meta.setAttribute('content', withResizesContent(meta.getAttribute('content')));

  const vv = win.visualViewport;
  const reveal = () => {
    const el = doc.activeElement;
    if (!isTextEntryElement(el) || typeof el.getBoundingClientRect !== 'function') return;
    const viewport = vv ? { height: vv.height, offsetTop: vv.offsetTop } : { height: win.innerHeight, offsetTop: 0 };
    if (!isHiddenByKeyboard(el.getBoundingClientRect(), viewport)) return;
    el.scrollIntoView?.({ block: 'center', inline: 'nearest' });
  };

  const timers = new Set();
  const later = (ms) => {
    const t = win.setTimeout(() => { timers.delete(t); reveal(); }, ms);
    timers.add(t);
  };
  const onFocusIn = (event) => {
    if (!isTextEntryElement(event.target)) return;
    // The keyboard animates in over ~250ms; check after it lands and once more.
    later(300);
    later(700);
  };

  doc.addEventListener('focusin', onFocusIn);
  vv?.addEventListener?.('resize', reveal);
  return () => {
    doc.removeEventListener('focusin', onFocusIn);
    vv?.removeEventListener?.('resize', reveal);
    timers.forEach((t) => win.clearTimeout(t));
    win.__vrcKeyboardFocusInstalled = false;
  };
}
